import { useEffect, useMemo, useState } from "react";
import api, { fetchBlobUrl } from "@/lib/api";
import Layout from "@/components/Layout";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  AlertCircle, CheckCircle2, User as UserIcon, Stethoscope, Activity, Wallet,
  Camera, Phone, Mail, Hash, Calendar, MapPin, Save, RefreshCw, Link2, Unlink, Check, Bell, Pencil, X,
} from "lucide-react";
import { validPositionsFor, validPositionsForCategory } from "@/lib/positions";
import { isStaff, isAdminLike, CATEGORY_LABEL } from "@/lib/roles";
import { validateRut, formatRut, validatePhone, formatPhone } from "@/lib/validation";
import { fmtDate as fmtDateSafe } from "@/lib/dates";
import { isPushSupported, currentPermission, subscribeUser, unsubscribeUser } from "@/lib/push";
import { toast as toast2 } from "sonner"; // eslint-disable-line no-unused-vars
import CredentialCard from "@/components/CredentialCard";
import { CLUB } from "@/lib/club";

const CAT_LABEL = {
  tackle: "Tackle",
  flag_masculino: "Flag Masculino",
  flag_femenino: "Flag Femenino",
};

const STATUS_META = {
  active: { label: "Activo", color: "#34C759" },
  injured: { label: "Lesionado", color: "#FF9F0A" },
  suspended: { label: "Suspendido", color: "#FF3B30" },
};

const TABS = [
  { key: "summary", label: "Resumen", icon: UserIcon },
  { key: "medical", label: "Médico", icon: Stethoscope },
  { key: "attendance", label: "Asistencia", icon: Activity },
  { key: "finance", label: "Finanzas", icon: Wallet },
];

const fmtMoney = (n) => `$${Math.round(n || 0).toLocaleString("es-CL")}`;
const fmtDate = (iso) => fmtDateSafe(iso, { day: "2-digit", month: "short", year: "numeric" });

export default function Profile() {
  const { user, refresh, refreshProfile, onboardingCompleted } = useAuth();
  const [profile, setProfile] = useState({});
  const [photoUrl, setPhotoUrl] = useState(null);
  const [activeTab, setActiveTab] = useState("summary");
  const [saving, setSaving] = useState(false);

  const positionOptions = useMemo(() => validPositionsFor(profile.categories), [profile.categories]);
  const missingRut = !((profile.rut || "").trim());
  const missingPhone = !((profile.phone || "").trim());
  const missingEmergencyContact = !((profile.emergency_contact || "").trim());
  const missingEmergencyRelationship = !((profile.emergency_relationship || "").trim());
  const missingEmergency = !((profile.emergency_phone || "").trim());
  const missingBirthDate = !((profile.birth_date || "").trim());
  const incomplete = missingRut || missingPhone || missingEmergencyContact || missingEmergencyRelationship || missingEmergency || missingBirthDate;
  const canEditMedNotes = isStaff(user?.role);
  const canEditAssignment = isStaff(user?.role); // categorías, camiseta, posiciones, estado

  const loadPhoto = async (profileData) => {
    // `fetchBlobUrl` usa fetch nativo en vez de axios para evitar el bug
    // "responseText is not accessible when responseType is 'blob'" que mostraba
    // un overlay rojo en CRA cuando Drive devolvía 4xx (token expirado).
    if (profileData?.photo_drive_id) {
      setPhotoUrl(await fetchBlobUrl(`/drive/file/${profileData.photo_drive_id}`));
      return;
    }
    if (profileData?.photo_path) {
      setPhotoUrl(await fetchBlobUrl(`/files/${profileData.photo_path}`));
      return;
    }
    setPhotoUrl(null);
  };

  useEffect(() => {
    api.get("/players/me").then(r => {
      setProfile(r.data);
      loadPhoto(r.data);
    });
  }, []);

  const update = (k, v) => setProfile(p => ({ ...p, [k]: v }));
  const toggleCategory = (cat) => {
    const next = (profile.categories || []).includes(cat)
      ? profile.categories.filter(c => c !== cat)
      : [...(profile.categories || []), cat];
    update("categories", next);
  };
  // Updates ONE field of ONE category inside per_category.
  // E.g. updatePerCat("tackle", "jersey_number", 12)
  const updatePerCat = (cat, field, value) => {
    setProfile(p => {
      const current = (p.per_category && p.per_category[cat]) || {};
      const nextSub = { ...current };
      if (value === "" || value === null || value === undefined) delete nextSub[field];
      else nextSub[field] = value;
      const next = { ...(p.per_category || {}), [cat]: nextSub };
      // Strip empty buckets so we don't pollute mongo
      if (Object.keys(nextSub).length === 0) delete next[cat];
      return { ...p, per_category: next };
    });
  };

  const validationErrors = useMemo(() => {
    const errs = {};
    if (profile.rut && !validateRut(profile.rut)) errs.rut = "RUT inválido (verifica el dígito)";
    if (profile.phone && !validatePhone(profile.phone)) errs.phone = "Teléfono inválido (+56 9 XXXX XXXX)";
    if (profile.emergency_phone && !validatePhone(profile.emergency_phone)) errs.emergency_phone = "Teléfono inválido (+56 9 XXXX XXXX)";
    return errs;
  }, [profile.rut, profile.phone, profile.emergency_phone]);

  const save = async () => {
    if (Object.keys(validationErrors).length) {
      toast.error("Corrige los datos marcados antes de guardar");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...profile };
      // Formatos canónicos antes de persistir
      if (payload.rut) payload.rut = formatRut(payload.rut);
      if (payload.phone) payload.phone = formatPhone(payload.phone);
      if (payload.emergency_phone) payload.emergency_phone = formatPhone(payload.emergency_phone);
      ["jersey_number", "height_cm", "weight_kg"].forEach(k => {
        if (payload[k] === "" || payload[k] === null) delete payload[k];
        else if (payload[k] !== undefined) payload[k] = Number(payload[k]);
      });
      // Coerce per_category.jersey_number to Number; drop empty subkeys
      if (payload.per_category && typeof payload.per_category === "object") {
        const cleaned = {};
        Object.entries(payload.per_category).forEach(([cat, sub]) => {
          if (!sub || typeof sub !== "object") return;
          const out = {};
          if (sub.jersey_number !== "" && sub.jersey_number !== null && sub.jersey_number !== undefined) {
            const n = Number(sub.jersey_number);
            if (!Number.isNaN(n)) out.jersey_number = n;
          }
          if (sub.position) out.position = sub.position;
          if (sub.position_secondary) out.position_secondary = sub.position_secondary;
          if (Object.keys(out).length) cleaned[cat] = out;
        });
        payload.per_category = cleaned;
      }
      await api.put("/players/me", payload);
      // refleja el formato en el estado
      setProfile(payload);
      toast.success("Ficha actualizada");
      await refresh();
      await refreshProfile();
    } catch { toast.error("Error al guardar"); }
    finally { setSaving(false); }
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/players/me/photo", fd);
      const updated = {
        ...profile,
        photo_drive_id: data.drive_file_id,
        photo_drive_link: data.drive_view_link,
        photo_drive_name: data.filename,
        photo_path: undefined,
      };
      setProfile(updated);
      loadPhoto(updated);
      toast.success("Foto actualizada · guardada en Drive del club");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Error al subir la foto";
      toast.error(detail);
    }
  };

  const status = STATUS_META[profile.status || "active"];
  const primaryCategory = (profile.categories || [])[0];
  const roleLabel = roleLabelFor(user?.role, primaryCategory);

  return (
    <Layout>
      <div className="px-6 md:px-10 py-8 max-w-[1500px]">
        <div className="mb-6">
          <div className="overline">Player File</div>
          <h1 className="font-display text-4xl sm:text-5xl text-white leading-none">MI FICHA</h1>
        </div>

        <PushSettingsCard/>

        {incomplete && (
          <div className="tactical-card p-4 mb-6 flex items-start gap-3" style={{ borderColor: "#FF9F0A", background: "linear-gradient(120deg, rgba(255,159,10,0.10) 0%, transparent 60%)" }} data-testid="onboarding-banner">
            <AlertCircle size={18} className="text-[#FF9F0A] flex-shrink-0 mt-0.5"/>
            <div>
              <div className="overline text-[#FF9F0A]">Completa tu ficha</div>
              <div className="text-sm text-white mt-1">Necesitas llenar: {[
                missingRut && "RUT",
                missingPhone && "Teléfono",
                missingEmergencyContact && "Contacto de emergencia",
                missingEmergency && "Teléfono de emergencia",
              ].filter(Boolean).join(", ")}</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-6">
          {/* SIDEBAR */}
          <aside className="space-y-4">
            {/* Profile card */}
            <div className="tactical-card p-5 text-center" data-testid="profile-sidebar">
              {/* Photo */}
              <div className="relative w-32 h-32 mx-auto mb-4">
                <div className="w-32 h-32 rounded-full overflow-hidden border-2 border-cyan-400/40 shadow-lg shadow-cyan-500/20" data-testid="profile-photo-wrap">
                  {photoUrl ? (
                    <img src={photoUrl} alt="" className="w-full h-full object-cover" data-testid="profile-photo"/>
                  ) : (
                    <div className="w-full h-full bg-[#1A1A1A] flex items-center justify-center font-display text-5xl text-cyan-400">
                      {user?.name?.[0]}
                    </div>
                  )}
                </div>
                <label className="absolute bottom-0 right-0 w-9 h-9 rounded-full bg-cyan-500 text-black flex items-center justify-center cursor-pointer hover:bg-cyan-400 transition-colors" data-testid="upload-photo-label">
                  <Camera size={14}/>
                  <input type="file" accept="image/*" className="hidden" onChange={uploadPhoto} data-testid="upload-photo-input"/>
                </label>
              </div>

              {/* Name + role */}
              <EditableName user={user} refresh={refresh}/>
              <div className="text-xs text-cyan-400 uppercase tracking-widest font-bold mt-1">{roleLabel}</div>

              {/* Status chip */}
              <div className="inline-flex items-center gap-1.5 mt-3 px-3 py-1 text-[10px] uppercase tracking-widest font-bold" style={{ background: `${status.color}20`, color: status.color, border: `1px solid ${status.color}` }} data-testid="status-chip">
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: status.color }}/>
                {status.label}
              </div>

              {/* Status switcher (compact) — solo Staff/Admin pueden cambiar */}
              <div className="grid grid-cols-3 gap-1 mt-4">
                {Object.entries(STATUS_META).map(([k, s]) => {
                  const active = (profile.status || "active") === k;
                  return (
                    <button
                      key={k}
                      onClick={() => canEditAssignment && update("status", k)}
                      disabled={!canEditAssignment}
                      data-testid={`status-${k}`}
                      title={canEditAssignment ? "" : "Solo Coach/Admin puede cambiar el estado"}
                      className={`px-2 py-1.5 text-[9px] uppercase tracking-wider font-bold border transition-colors ${active ? "bg-[#1A1A1A]" : "border-[#27272A] text-[#A1A1AA] hover:text-white"} ${!canEditAssignment ? "opacity-60 cursor-not-allowed hover:text-[#A1A1AA]" : ""}`}
                      style={active ? { borderColor: s.color, color: s.color } : {}}
                    >
                      {s.label}
                    </button>
                  );
                })}
              </div>
              {!canEditAssignment && (
                <div className="text-[9px] uppercase tracking-widest text-[#52525b] mt-2">
                  Estado asignado por Coach/Admin
                </div>
              )}
            </div>

            {/* Credential downloader */}
            <div className="tactical-card p-4" data-testid="credential-wrap">
              <div className="overline text-cyan-400 mb-2">Credencial digital</div>
              <CredentialCard user={user} profile={profile} photoUrl={photoUrl} clubLogo={CLUB.logo} clubName={CLUB.name}/>
            </div>

            {/* Quick data */}
            <div className="tactical-card p-4 space-y-3" data-testid="quick-data">
              <div className="overline">Datos de contacto</div>
              <QuickRow icon={Hash} label="RUT" value={profile.rut || "—"} highlight={missingRut}/>
              <QuickRow icon={Mail} label="Email" value={user?.email}/>
              <QuickRow icon={Phone} label="Emergencia" value={profile.emergency_phone || "—"} highlight={missingEmergency}/>
            </div>

            {/* Google Calendar sync */}
            <CalendarConnectCard/>
          </aside>

          {/* MAIN PANEL */}
          <main>
            {/* Tabs */}
            <div className="flex border border-[#27272A] mb-4 overflow-x-auto" data-testid="profile-tabs">
              {TABS.map(t => {
                const Icon = t.icon;
                const active = activeTab === t.key;
                return (
                  <button
                    key={t.key}
                    onClick={() => setActiveTab(t.key)}
                    data-testid={`tab-${t.key}`}
                    className={`flex items-center gap-2 px-5 py-3 text-xs uppercase tracking-wider font-bold border-r border-[#27272A] last:border-r-0 transition-colors ${active ? "text-cyan-400 bg-[#0a1a2e] border-b-2 border-b-cyan-400" : "text-[#A1A1AA] hover:text-white"}`}
                  >
                    <Icon size={14}/> {t.label}
                  </button>
                );
              })}
            </div>

            {activeTab === "summary" && (
              <SummaryTab profile={profile} update={update} updatePerCat={updatePerCat} toggleCategory={toggleCategory} positionOptions={positionOptions} missingRut={missingRut} missingPhone={missingPhone} missingEmergencyContact={missingEmergencyContact} missingEmergencyRelationship={missingEmergencyRelationship} missingEmergency={missingEmergency} missingBirthDate={missingBirthDate} save={save} saving={saving} canEditAssignment={canEditAssignment} errors={validationErrors}/>
            )}
            {activeTab === "medical" && (
              <MedicalTab profile={profile} update={update} canEditNotes={canEditMedNotes} save={save} saving={saving}/>
            )}
            {activeTab === "attendance" && <AttendanceTab/>}
            {activeTab === "finance" && <FinanceTab/>}
          </main>
        </div>

        <div className="h-20 md:h-6"/>
      </div>
    </Layout>
  );
}

// =================== TAB: SUMMARY ===================
function SummaryTab({ profile, update, updatePerCat, toggleCategory, positionOptions, missingRut, missingPhone, missingEmergencyContact, missingEmergencyRelationship, missingEmergency, missingBirthDate, save, saving, canEditAssignment, errors = {} }) {
  const cats = profile.categories || [];
  const multiCat = cats.length >= 2;
  return (
    <div className="space-y-4">
      <Card title="Categorías / Equipos" data-testid="card-categories">
        <p className="text-xs text-[#71717A] mb-3">
          {canEditAssignment
            ? "Selecciona todas las ramas a las que perteneces."
            : "Las ramas son asignadas por Coach/Admin. Si necesitas cambios, contacta a la directiva."}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {Object.entries(CAT_LABEL).map(([k, l]) => {
            const active = (profile.categories || []).includes(k);
            return (
              <button
                key={k}
                onClick={() => canEditAssignment && toggleCategory(k)}
                disabled={!canEditAssignment}
                data-testid={`cat-toggle-${k}`}
                title={canEditAssignment ? "" : "Solo Coach/Admin puede asignar ramas"}
                className={`px-4 py-3 border text-xs uppercase tracking-widest font-bold transition-colors ${active ? "border-cyan-400 bg-[#0a1a2e] text-white" : "border-[#27272A] text-[#A1A1AA]"} ${canEditAssignment ? "hover:text-white" : "opacity-70 cursor-not-allowed"}`}
              >
                {l}
              </button>
            );
          })}
        </div>
      </Card>

      <Card title="Datos deportivos">
        {!canEditAssignment && (
          <div className="text-[10px] uppercase tracking-widest text-[#52525b] mb-3 flex items-center gap-1">
            🔒 Camiseta y posiciones asignadas por Coach/Admin
          </div>
        )}
        {multiCat ? (
          <div className="space-y-4 mb-4" data-testid="per-cat-block">
            <div className="text-[10px] uppercase tracking-widest text-[#FACC15] flex items-center gap-2">
              Camiseta y posición por categoría
              <span className="text-[#71717A] normal-case tracking-normal text-[11px]">— juegas en {cats.length} ramas, configura cada una</span>
            </div>
            {cats.map((cat) => {
              const opts = validPositionsForCategory(cat);
              const sub = (profile.per_category && profile.per_category[cat]) || {};
              return (
                <div key={cat} className="border border-[#27272A] p-4 bg-[#0F0F0F]" data-testid={`per-cat-${cat}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-[10px] uppercase tracking-widest font-bold text-white border border-[#007AFF] text-[#007AFF] px-2 py-0.5">
                      {CATEGORY_LABEL[cat] || cat}
                    </span>
                  </div>
                  <Grid>
                    <Field label="Nº de camiseta">
                      <input
                        type="number"
                        className={`input-tactical ${!canEditAssignment ? "opacity-60 cursor-not-allowed" : ""}`}
                        value={sub.jersey_number ?? ""}
                        onChange={e => canEditAssignment && updatePerCat(cat, "jersey_number", e.target.value)}
                        disabled={!canEditAssignment}
                        data-testid={`jersey-${cat}`}
                      />
                    </Field>
                    <Field label="Posición principal">
                      <select
                        className={`input-tactical ${!canEditAssignment ? "opacity-60 cursor-not-allowed" : ""}`}
                        value={sub.position ?? ""}
                        onChange={e => canEditAssignment && updatePerCat(cat, "position", e.target.value)}
                        disabled={!canEditAssignment}
                        data-testid={`pos-${cat}`}
                      >
                        <option value="">Selecciona...</option>
                        {opts.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                      </select>
                    </Field>
                    <Field label="Posición secundaria">
                      <select
                        className={`input-tactical ${!canEditAssignment ? "opacity-60 cursor-not-allowed" : ""}`}
                        value={sub.position_secondary ?? ""}
                        onChange={e => canEditAssignment && updatePerCat(cat, "position_secondary", e.target.value)}
                        disabled={!canEditAssignment}
                        data-testid={`pos2-${cat}`}
                      >
                        <option value="">(opcional)</option>
                        {opts.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                      </select>
                    </Field>
                  </Grid>
                </div>
              );
            })}
            <div className="text-[10px] text-[#71717A]">
              El número y posición de tu credencial usa los datos de <span className="text-white font-bold">{CATEGORY_LABEL[cats[0]] || cats[0]}</span> (categoría principal). Coach/Admin pueden cambiarlos.
            </div>
          </div>
        ) : (
          <Grid>
            <Field label="Número de camiseta">
              <input
                type="number"
                className={`input-tactical ${!canEditAssignment ? "opacity-60 cursor-not-allowed" : ""}`}
                value={profile.jersey_number ?? ""}
                onChange={e => canEditAssignment && update("jersey_number", e.target.value)}
                disabled={!canEditAssignment}
                data-testid="jersey-input"
              />
            </Field>
            <Field label="Posición principal">
              <select
                className={`input-tactical ${!canEditAssignment ? "opacity-60 cursor-not-allowed" : ""}`}
                value={profile.position ?? ""}
                onChange={e => canEditAssignment && update("position", e.target.value)}
                disabled={!canEditAssignment}
                data-testid="position-select"
              >
                <option value="">Selecciona tu posición...</option>
                {positionOptions.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </Field>
            <Field label="Posición secundaria">
              <select
                className={`input-tactical ${!canEditAssignment ? "opacity-60 cursor-not-allowed" : ""}`}
                value={profile.position_secondary ?? ""}
                onChange={e => canEditAssignment && update("position_secondary", e.target.value)}
                disabled={!canEditAssignment}
                data-testid="position-secondary-select"
              >
                <option value="">(opcional)</option>
                {positionOptions.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </Field>
          </Grid>
        )}
        <Grid>
          <Field label="Altura (cm)">
            <input type="number" className="input-tactical" value={profile.height_cm ?? ""} onChange={e => update("height_cm", e.target.value)}/>
          </Field>
          <Field label="Peso (kg)">
            <input type="number" className="input-tactical" value={profile.weight_kg ?? ""} onChange={e => update("weight_kg", e.target.value)}/>
          </Field>
          <Field label={<>Fecha de nacimiento <span className="text-[#FF3B30]">*</span></>}>
            <input
              required
              type="date"
              className={`input-tactical ${missingBirthDate ? "border-[#FF9F0A]" : ""}`}
              value={profile.birth_date ?? ""}
              onChange={e => update("birth_date", e.target.value)}
              data-testid="birth-date-input"
            />
          </Field>
        </Grid>
      </Card>

      <Card title="Datos personales">
        <Grid>
          <Field label={<>RUT <span className="text-[#FF3B30]">*</span></>}>
            <input
              required
              className={`input-tactical ${errors.rut ? "border-[#FF3B30]" : missingRut ? "border-[#FF9F0A]" : ""}`}
              value={profile.rut ?? ""}
              onChange={e => update("rut", e.target.value)}
              onBlur={e => { if (e.target.value && !errors.rut) update("rut", formatRut(e.target.value)); }}
              placeholder="12.345.678-9"
              data-testid="rut-input"
            />
            {errors.rut && <div className="text-[10px] text-[#FF3B30] mt-1" data-testid="rut-error">{errors.rut}</div>}
          </Field>
          <Field label={<>Teléfono <span className="text-[#FF3B30]">*</span></>}>
            <input
              required
              className={`input-tactical ${errors.phone ? "border-[#FF3B30]" : missingPhone ? "border-[#FF9F0A]" : ""}`}
              value={profile.phone ?? ""}
              onChange={e => update("phone", e.target.value)}
              onBlur={e => { if (e.target.value && !errors.phone) update("phone", formatPhone(e.target.value)); }}
              placeholder="+56 9 1234 5678"
              data-testid="phone-input"
            />
            {errors.phone && <div className="text-[10px] text-[#FF3B30] mt-1" data-testid="phone-error">{errors.phone}</div>}
          </Field>
          <Field label={<>Contacto de emergencia <span className="text-[#FF3B30]">*</span></>}>
            <input
              required
              className={`input-tactical ${missingEmergencyContact ? "border-[#FF9F0A]" : ""}`}
              value={profile.emergency_contact ?? ""}
              onChange={e => update("emergency_contact", e.target.value)}
              placeholder="Nombre completo (ej: María González)"
              data-testid="emergency-contact-input"
            />
          </Field>
          <Field label={<>Parentesco <span className="text-[#FF3B30]">*</span></>}>
            <RelationshipField
              value={profile.emergency_relationship ?? ""}
              onChange={(v) => update("emergency_relationship", v)}
              error={missingEmergencyRelationship}
            />
          </Field>
          <Field label={<>Teléfono emergencia <span className="text-[#FF3B30]">*</span></>}>
            <input
              required
              className={`input-tactical ${errors.emergency_phone ? "border-[#FF3B30]" : missingEmergency ? "border-[#FF9F0A]" : ""}`}
              value={profile.emergency_phone ?? ""}
              onChange={e => update("emergency_phone", e.target.value)}
              onBlur={e => { if (e.target.value && !errors.emergency_phone) update("emergency_phone", formatPhone(e.target.value)); }}
              placeholder="+56 9 1234 5678"
              data-testid="emergency-phone-input"
            />
            {errors.emergency_phone && <div className="text-[10px] text-[#FF3B30] mt-1" data-testid="emergency-phone-error">{errors.emergency_phone}</div>}
          </Field>
        </Grid>
      </Card>

      <SaveBar save={save} saving={saving} disabled={Object.keys(errors).length > 0}/>
    </div>
  );
}

// =================== TAB: MEDICAL ===================
function MedicalTab({ profile, update, canEditNotes, save, saving }) {
  return (
    <div className="space-y-4">
      <div className="tactical-card p-3 flex items-center gap-2 text-xs text-[#A78BFA] uppercase tracking-wider">
        <Stethoscope size={12}/> Privacidad · Visible solo para Staff y Administración
      </div>

      <Card title="Datos médicos">
        <Grid>
          <Field label="Grupo sanguíneo">
            <select className="input-tactical" value={profile.blood_type ?? ""} onChange={e => update("blood_type", e.target.value)} data-testid="blood-type">
              <option value="">Seleccionar...</option>
              {["O+", "O-", "A+", "A-", "B+", "B-", "AB+", "AB-"].map(b => <option key={b} value={b}>{b}</option>)}
            </select>
          </Field>
          <Field label="Cobertura de salud">
            <select className="input-tactical" value={profile.health_coverage ?? ""} onChange={e => update("health_coverage", e.target.value)} data-testid="health-coverage">
              <option value="">Seleccionar...</option>
              <option value="fonasa">Fonasa</option>
              <option value="isapre">Isapre</option>
              <option value="otro">Otro</option>
              <option value="sin">Sin cobertura</option>
            </select>
          </Field>
          <Field label="Plan / Isapre">
            <input className="input-tactical" value={profile.health_plan ?? ""} onChange={e => update("health_plan", e.target.value)} placeholder="Ej: Banmédica, Colmena..."/>
          </Field>
          <Field label="Alergias">
            <input className="input-tactical" value={profile.allergies ?? ""} onChange={e => update("allergies", e.target.value)} data-testid="allergies"/>
          </Field>
        </Grid>
      </Card>

      <Card title={canEditNotes ? "Notas médicas (editable por Staff)" : "Notas médicas (solo lectura · Staff)"}>
        <textarea
          rows={5}
          disabled={!canEditNotes}
          className={`input-tactical ${!canEditNotes ? "opacity-60 cursor-not-allowed" : ""}`}
          value={profile.medical_notes ?? ""}
          onChange={e => update("medical_notes", e.target.value)}
          placeholder={canEditNotes ? "Lesiones previas, condiciones especiales, medicación..." : "Sin notas registradas."}
          data-testid="medical-notes"
        />
        {!canEditNotes && (
          <div className="text-[10px] text-[#71717A] mt-2 uppercase tracking-wider">
            Estas notas son confidenciales y solo pueden editarse por el staff técnico.
          </div>
        )}
      </Card>

      <SaveBar save={save} saving={saving}/>
    </div>
  );
}

// =================== TAB: ATTENDANCE ===================
function AttendanceTab() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/players/me/attendance").then(r => setData(r.data)).catch(() => setData({}));
  }, []);

  if (!data) return <div className="tactical-card p-8 text-center text-[#A1A1AA]">Cargando...</div>;
  if (!data.total_events) {
    return <div className="tactical-card p-10 text-center text-[#A1A1AA]">Aún no hay eventos pasados en tu rama. Tu asistencia validada aparecerá aquí.</div>;
  }

  const { attendance_rate, going_rate, by_status, by_type, recent, total_events, attended, validated_events } = data;
  const noValidated = !validated_events;

  return (
    <div className="space-y-4" data-testid="attendance-tab">
      {noValidated && (
        <div className="tactical-card p-3 text-xs text-[#FF9F0A] uppercase tracking-wider" data-testid="att-pending-note">
          Aún no hay asistencias validadas por el coach. Los porcentajes aparecerán cuando se valide al menos un evento.
        </div>
      )}
      {/* Big stat */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-[#27272A]">
        <BigStat label="Asistencia validada" value={`${attendance_rate}%`} sub={`${attended} de ${validated_events || 0}`} color="#22d3ee"/>
        <BigStat label="A tiempo" value={`${going_rate}%`} sub={`${by_status.going} eventos`} color="#34C759"/>
        <BigStat label="Ausencias" value={by_status.not_going} sub={`${by_status.justified || 0} justificadas`} color="#FF3B30"/>
      </div>

      {/* Progress bar visualization */}
      <div className="tactical-card p-5">
        <div className="overline mb-3">Distribución (validados {validated_events || 0} de {total_events})</div>
        <div className="space-y-2">
          {[
            { key: "going", label: "Asistí", color: "#34C759", count: by_status.going },
            { key: "late", label: "Llegué tarde", color: "#FF9F0A", count: by_status.late },
            { key: "justified", label: "Ausencia justificada", color: "#A78BFA", count: by_status.justified || 0 },
            { key: "not_going", label: "Ausente", color: "#FF3B30", count: by_status.not_going },
            { key: "no_response", label: "Sin validar", color: "#71717A", count: by_status.no_response },
          ].map(r => {
            const pct = total_events ? Math.round((r.count / total_events) * 100) : 0;
            return (
              <div key={r.key} className="grid grid-cols-[160px_1fr_80px] items-center gap-3">
                <div className="text-xs text-[#A1A1AA]">{r.label}</div>
                <div className="h-3 bg-[#0A0A0A] relative">
                  <div className="h-3" style={{ width: `${pct}%`, background: r.color }}/>
                </div>
                <div className="text-xs font-mono text-white text-right">{r.count} ({pct}%)</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* By type */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {Object.entries(by_type).filter(([, v]) => (v.validated || v.total) > 0).map(([type, v]) => {
          const den = v.validated || v.total;
          const rate = den ? Math.round(((v.going + v.late) / den) * 100) : 0;
          return (
            <div key={type} className="tactical-card p-4">
              <div className="overline mb-2">{typeLabel(type)}</div>
              <div className="flex items-end justify-between">
                <div className="font-display text-3xl text-white">{rate}%</div>
                <div className="text-xs text-[#A1A1AA]">{v.going + v.late} / {den} validados</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Recent events */}
      <div className="tactical-card overflow-x-auto">
        <div className="p-4 border-b border-[#27272A]">
          <div className="overline">Últimos eventos</div>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#27272A]">
              <th className="text-left p-3 overline">Fecha</th>
              <th className="text-left p-3 overline">Evento</th>
              <th className="text-left p-3 overline">Tipo</th>
              <th className="text-left p-3 overline">Estado validado</th>
            </tr>
          </thead>
          <tbody>
            {recent.map(ev => (
              <tr key={ev.id} className="border-b border-[#27272A]" data-testid={`att-row-${ev.id}`}>
                <td className="p-3 font-mono text-xs text-white">{fmtDate(ev.starts_at)}</td>
                <td className="p-3 text-white">{ev.title}</td>
                <td className="p-3 text-xs text-[#A1A1AA]">{typeLabel(ev.event_type)}</td>
                <td className="p-3"><StatusBadge status={ev.status}/></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function typeLabel(t) { return { practice: "Entrenamientos", game: "Partidos", meeting: "Reuniones", other: "Actividades" }[t] || t; }
function StatusBadge({ status }) {
  const meta = {
    going: { label: "Asistió", c: "#34C759" },
    late: { label: "Tarde", c: "#FF9F0A" },
    not_going: { label: "Ausente", c: "#FF3B30" },
    justified: { label: "Justificada", c: "#A78BFA" },
    no_response: { label: "Sin validar", c: "#71717A" },
  }[status] || { label: status, c: "#71717A" };
  return <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 border" style={{ borderColor: meta.c, color: meta.c }}>{meta.label}</span>;
}

// =================== TAB: FINANCE ===================
function FinanceTab() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/finance/charges/me").then(r => setData(r.data)).catch(() => setData({ charges: [] }));
  }, []);

  if (!data) return <div className="tactical-card p-8 text-center text-[#A1A1AA]">Cargando...</div>;

  return (
    <div className="space-y-4" data-testid="finance-tab">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-[#27272A]">
        <BigStat label="Deuda actual" value={fmtMoney(data.pending_total)} sub={`${data.pending_count || 0} pendientes`} color="#FF9F0A"/>
        <BigStat label="Por validar" value={data.validation_count || 0} sub="Esperando tesorero" color="#A78BFA"/>
        <BigStat label="Pagado total" value={fmtMoney(data.paid_total)} sub="Histórico" color="#34C759"/>
      </div>

      <div className="tactical-card overflow-x-auto">
        <div className="p-4 border-b border-[#27272A] flex items-center justify-between">
          <div className="overline">Historial de cobros</div>
          <a href="/dues" className="text-[10px] uppercase tracking-wider text-cyan-400 hover:text-white">→ Ir a Mis Cuotas</a>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#27272A]">
              <th className="text-left p-3 overline">Fecha</th>
              <th className="text-left p-3 overline">Concepto</th>
              <th className="text-left p-3 overline">Monto</th>
              <th className="text-left p-3 overline">Estado</th>
            </tr>
          </thead>
          <tbody>
            {(data.charges || []).length === 0 && (
              <tr><td colSpan={4} className="p-10 text-center text-[#71717A]">Sin movimientos.</td></tr>
            )}
            {(data.charges || []).map(c => (
              <tr key={c.id} className="border-b border-[#27272A]">
                <td className="p-3 font-mono text-xs text-white">{fmtDate(c.created_at)}</td>
                <td className="p-3 text-white">{c.concept}</td>
                <td className="p-3 font-mono">{fmtMoney(c.amount)}</td>
                <td className="p-3"><ChargeStatus s={c.status}/></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ChargeStatus({ s }) {
  const m = {
    pending: { l: "Pendiente", c: "#FF9F0A" },
    pending_validation: { l: "Por validar", c: "#A78BFA" },
    paid: { l: "Pagado", c: "#34C759" },
    rejected: { l: "Rechazado", c: "#FF3B30" },
  }[s] || { l: s, c: "#71717A" };
  return <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 border" style={{ borderColor: m.c, color: m.c }}>{m.l}</span>;
}

// =================== SHARED ===================
const Card = ({ title, children, "data-testid": dt }) => (
  <div className="tactical-card p-5" data-testid={dt}>
    <div className="overline mb-3">{title}</div>
    {children}
  </div>
);
const Grid = ({ children }) => <div className="grid md:grid-cols-2 gap-4">{children}</div>;
const Field = ({ label, children }) => (
  <div>
    <label className="label-tactical">{label}</label>
    {children}
  </div>
);

function QuickRow({ icon: Icon, label, value, highlight }) {
  return (
    <div className="flex items-start gap-2">
      <Icon size={12} className="text-cyan-400 mt-1 flex-shrink-0"/>
      <div className="min-w-0 flex-1">
        <div className="overline text-[#71717A]">{label}</div>
        <div className={`text-sm break-all ${highlight ? "text-[#FF9F0A]" : "text-white"}`}>{value}</div>
      </div>
    </div>
  );
}

function BigStat({ label, value, sub, color }) {
  return (
    <div className="p-5 border-r border-b border-[#27272A] last:border-r-0">
      <div className="overline">{label}</div>
      <div className="font-display text-3xl md:text-4xl mt-1" style={{ color: color || "#fff" }}>{value}</div>
      {sub && <div className="text-[10px] uppercase tracking-widest text-[#71717A] mt-1">{sub}</div>}
    </div>
  );
}

function SaveBar({ save, saving, disabled }) {
  return (
    <div className="flex justify-end items-center gap-3 pt-4 border-t border-[#27272A]">
      {disabled && (
        <span className="text-[10px] uppercase tracking-widest text-[#FF3B30]" data-testid="save-blocked">
          Corrige los datos marcados
        </span>
      )}
      <button onClick={save} disabled={saving || disabled} className="btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="save-profile-btn">
        <Save size={14}/> {saving ? "GUARDANDO..." : "GUARDAR FICHA"}
      </button>
    </div>
  );
}

function roleLabelFor(role, primaryCategory) {
  if (role === "player") {
    if (primaryCategory === "tackle") return "Jugador Tackle";
    if (primaryCategory === "flag_masculino") return "Jugador Flag Masc.";
    if (primaryCategory === "flag_femenino") return "Jugadora Flag Fem.";
    return "Jugador";
  }
  return {
    super_admin: "Super Admin",
    admin: "Administrador",
    admin_directive: "Admin Directivo",
    admin_treasurer: "Admin Tesorero",
    coach: "Coach",
    pending: "Pendiente",
  }[role] || role;
}


// =============== GOOGLE CALENDAR CONNECTION (per-user) ===============
function CalendarConnectCard() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const load = async () => {
    try { const r = await api.get("/calendar/status"); setStatus(r.data); }
    catch { setStatus({ connected: false }); }
  };

  useEffect(() => {
    load();
    // Si volvió del callback con ?calendar_connected=1, refrescar
    const sp = new URLSearchParams(window.location.search);
    if (sp.get("calendar_connected") === "1") {
      toast.success("Google Calendar conectado correctamente");
      const url = new URL(window.location.href);
      url.searchParams.delete("calendar_connected");
      window.history.replaceState({}, "", url.toString());
    }
  }, []);

  const connect = async () => {
    setBusy(true);
    try {
      const r = await api.get("/calendar/connect");
      window.location.href = r.data.authorization_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error iniciando conexión");
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!confirm("¿Desconectar tu Google Calendar?\n\nLos eventos ya sincronizados quedarán en tu calendario, pero las nuevas actualizaciones del club ya no se reflejarán automáticamente.")) return;
    setBusy(true);
    try {
      await api.post("/calendar/disconnect");
      toast.success("Calendar desconectado");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al desconectar");
    } finally { setBusy(false); }
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/calendar/sync");
      toast.success(`${r.data.events_queued} eventos en cola hacia tu Google Calendar`);
      setTimeout(load, 4000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al sincronizar");
    } finally { setSyncing(false); }
  };

  return (
    <div className="tactical-card p-4 space-y-3" data-testid="calendar-connect-card">
      <div className="flex items-center justify-between">
        <div className="overline flex items-center gap-1.5"><Calendar size={11}/> Google Calendar</div>
        {status?.connected && (
          <span className="text-[9px] uppercase tracking-widest text-[#34C759] flex items-center gap-1" data-testid="calendar-status-connected">
            <Check size={10}/> Conectado
          </span>
        )}
      </div>

      {status === null ? (
        <div className="text-xs text-[#71717A]">Cargando...</div>
      ) : status.connected ? (
        <>
          <div className="text-[11px] text-[#A1A1AA] leading-relaxed">
            Cuenta vinculada: <span className="text-white font-mono">{status.google_email || "Google account"}</span><br/>
            Eventos sincronizados: <span className="text-cyan-300 font-bold">{status.synced_events || 0}</span>
          </div>
          <div className="flex flex-col gap-2">
            <button
              onClick={sync}
              disabled={syncing}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 text-[10px] uppercase tracking-widest font-bold border border-cyan-400/60 text-cyan-400 hover:bg-cyan-400/10 disabled:opacity-50"
              data-testid="calendar-sync-btn"
            >
              <RefreshCw size={11} className={syncing ? "animate-spin" : ""}/>
              {syncing ? "Sincronizando..." : "Sincronizar ahora"}
            </button>
            <button
              onClick={disconnect}
              disabled={busy}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 text-[10px] uppercase tracking-widest font-bold border border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/10 disabled:opacity-50"
              data-testid="calendar-disconnect-btn"
            >
              <Unlink size={11}/> Desconectar
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="text-[11px] text-[#A1A1AA] leading-relaxed">
            Conecta tu cuenta de Google para que los <strong className="text-white">entrenamientos, partidos y reuniones</strong> del club aparezcan automáticamente en tu calendario personal.
          </div>
          <button
            onClick={connect}
            disabled={busy}
            className="w-full flex items-center justify-center gap-2 px-3 py-2.5 text-[11px] uppercase tracking-widest font-bold transition-colors disabled:opacity-50"
            style={{ background: "linear-gradient(90deg, #0891b2 0%, #06b6d4 100%)", color: "#0a0a0a" }}
            data-testid="calendar-connect-btn"
          >
            <Link2 size={12}/> {busy ? "Conectando..." : "Conectar Google Calendar"}
          </button>
          <div className="text-[9px] text-[#52525b] leading-snug">
            Sólo se solicitan permisos para crear/editar eventos en tu calendario primario. Podés desconectar en cualquier momento.
          </div>
        </>
      )}
    </div>
  );
}



// ---- Parentesco (emergency relationship) ----
// Las opciones predefinidas. Si el valor guardado no coincide con ninguna,
// se interpreta como "Otro" y se muestra el campo de texto libre.
const REL_OPTIONS = ["Madre", "Padre", "Hermano", "Hermana", "Pareja"];

function RelationshipField({ value, onChange, error }) {
  const isPreset = REL_OPTIONS.includes(value);
  // Recuerda explícitamente si el usuario seleccionó "Otro" aunque aún no haya
  // tipeado nada — así el text input no desaparece al abrir el modo otro.
  const [otherMode, setOtherMode] = useState(!isPreset && (value || "").trim().length > 0);

  const showOther = otherMode || (!isPreset && (value || "").trim().length > 0);
  const selectValue = isPreset && !otherMode ? value : (showOther ? "__other__" : "");

  const handleSelect = (e) => {
    const v = e.target.value;
    if (v === "__other__") {
      setOtherMode(true);
      // Si ya había un valor "otro" previo lo mantenemos; si era un preset lo limpiamos.
      if (isPreset) onChange("");
    } else {
      setOtherMode(false);
      onChange(v);
    }
  };

  return (
    <>
      <select
        required
        value={selectValue}
        onChange={handleSelect}
        className={`input-tactical ${error ? "border-[#FF9F0A]" : ""}`}
        data-testid="emergency-relationship-select"
      >
        <option value="">Selecciona...</option>
        {REL_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
        <option value="__other__">Otro</option>
      </select>
      {showOther && (
        <input
          required
          className={`input-tactical mt-2 ${!(value || "").trim() ? "border-[#FF9F0A]" : ""}`}
          value={value || ""}
          onChange={e => onChange(e.target.value)}
          placeholder="Indica el parentesco (ej: Tío, Amigo cercano...)"
          maxLength={60}
          data-testid="emergency-relationship-other-input"
        />
      )}
    </>
  );
}

function PushSettingsCard() {
  const [supported, setSupported] = useState(false);
  const [perm, setPerm] = useState("default");
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const refresh = async () => {
    const sup = isPushSupported();
    setSupported(sup);
    if (!sup) return;
    setPerm(currentPermission());
    try {
      const r = await api.get("/push/status");
      setSubscribed(!!r.data.subscribed);
    } catch { setSubscribed(false); }
  };
  useEffect(() => { refresh(); }, []);

  const activate = async () => {
    setBusy(true);
    const r = await subscribeUser();
    setBusy(false);
    await refresh();
    if (r.ok) toast.success("Notificaciones activadas ✓");
    else if (r.reason === "denied") toast.error("Permiso denegado. Habilítalo en la configuración del navegador y vuelve a intentar.");
    else if (r.reason === "notSupported") toast.error("Este navegador no soporta notificaciones.");
    else toast.error("No se pudo activar. Reintenta.");
  };
  const deactivate = async () => {
    if (!window.confirm("Desactivar notificaciones en este dispositivo?")) return;
    setBusy(true);
    await unsubscribeUser();
    setBusy(false);
    await refresh();
    toast.success("Notificaciones desactivadas en este dispositivo");
  };
  const sendTest = async () => {
    try { await api.post("/push/test"); toast.success("Enviando notificación de prueba…"); }
    catch { toast.error("No se pudo enviar la prueba"); }
  };

  if (!supported) return (
    <div className="tactical-card p-4 mb-6" data-testid="push-settings">
      <div className="flex items-center gap-3">
        <Bell size={16} className="text-[#71717A]"/>
        <div className="flex-1">
          <div className="text-white font-semibold text-sm">Notificaciones push</div>
          <div className="text-xs text-[#71717A]">Este navegador no soporta notificaciones. Prueba con Chrome, Firefox o Edge (o Safari en iOS 16.4+).</div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="tactical-card p-4 mb-6" data-testid="push-settings">
      <div className="flex items-start gap-3 flex-wrap">
        <Bell size={16} className={subscribed ? "text-cyan-400 mt-0.5" : "text-[#A1A1AA] mt-0.5"}/>
        <div className="flex-1 min-w-0">
          <div className="text-white font-semibold text-sm">Notificaciones push</div>
          <div className="text-xs text-[#A1A1AA] mt-0.5">
            {subscribed
              ? "Estás recibiendo avisos del club en este dispositivo."
              : perm === "denied"
                ? "Permiso bloqueado. Ábrelo desde la configuración del navegador (candado en la barra de direcciones)."
                : "Recibe avisos de nuevos partidos, cambios en el fixture y publicaciones en Somos Ferro."}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {subscribed ? (
            <>
              <button onClick={sendTest} className="text-[10px] uppercase tracking-widest px-3 py-1.5 border border-[#27272A] text-[#A1A1AA] hover:text-white hover:border-cyan-400" data-testid="push-test">Enviar prueba</button>
              <button onClick={deactivate} disabled={busy} className="text-[10px] uppercase tracking-widest px-3 py-1.5 border border-[#FF9F0A] text-[#FF9F0A] hover:bg-[#FF9F0A]/10" data-testid="push-deactivate">{busy ? "…" : "Desactivar"}</button>
            </>
          ) : (
            <button onClick={activate} disabled={busy || perm === "denied"} className="text-[10px] uppercase tracking-widest px-3 py-1.5 border border-cyan-400 text-cyan-400 hover:bg-cyan-400/10 disabled:opacity-40 disabled:cursor-not-allowed" data-testid="push-activate">{busy ? "Activando…" : "Activar notificaciones"}</button>
          )}
        </div>
      </div>
    </div>
  );
}


// =============== EDITABLE NAME (Mi ficha) ===============
function EditableName({ user, refresh }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(user?.name || "");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setValue(user?.name || ""); }, [user?.name]);

  const cancel = () => {
    setValue(user?.name || "");
    setEditing(false);
  };

  const save = async () => {
    const trimmed = (value || "").trim();
    if (!trimmed) { toast.error("El nombre no puede estar vacío"); return; }
    if (trimmed.length > 80) { toast.error("Máximo 80 caracteres"); return; }
    if (trimmed === user?.name) { setEditing(false); return; }
    setSaving(true);
    try {
      await api.patch("/auth/me/name", { name: trimmed });
      toast.success("Nombre actualizado");
      await refresh();
      setEditing(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al actualizar el nombre");
    } finally { setSaving(false); }
  };

  if (editing) {
    return (
      <div className="space-y-2" data-testid="edit-name-form">
        <input
          className="input-tactical text-center text-lg font-display uppercase tracking-tight"
          value={value}
          onChange={e => setValue(e.target.value)}
          maxLength={80}
          autoFocus
          disabled={saving}
          onKeyDown={e => { if (e.key === "Enter") save(); if (e.key === "Escape") cancel(); }}
          data-testid="edit-name-input"
        />
        <div className="flex gap-2 justify-center">
          <button
            onClick={save}
            disabled={saving}
            className="flex items-center gap-1 px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold border border-cyan-400 text-cyan-400 hover:bg-cyan-400/10 disabled:opacity-50"
            data-testid="save-name-btn"
          >
            <Check size={11}/> {saving ? "..." : "Guardar"}
          </button>
          <button
            onClick={cancel}
            disabled={saving}
            className="flex items-center gap-1 px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold border border-[#27272A] text-[#A1A1AA] hover:text-white disabled:opacity-50"
            data-testid="cancel-name-btn"
          >
            <X size={11}/> Cancelar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center gap-2 group" data-testid="display-name-wrap">
      <div className="font-display text-2xl text-white leading-tight" data-testid="display-name">
        {(user?.name || "").toUpperCase()}
      </div>
      <button
        onClick={() => setEditing(true)}
        className="text-[#71717A] hover:text-cyan-400 transition-colors opacity-60 hover:opacity-100"
        title="Editar nombre"
        data-testid="edit-name-btn"
      >
        <Pencil size={13}/>
      </button>
    </div>
  );
}
